import React from "react";
import { Pagination } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

const SimplePagination = ({
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  onPageChange,
  onPreviousPage,
  onNextPage,
  canPreviousPage,
  canNextPage,
  onPageSizeChange,
  pageSizeOptions,
}) => {
  const { t } = useTranslation("common");
  
  // Calculate page range to show (max 5 pages)
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(0, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages - 1, start + maxVisible - 1);
    
    if (end - start < maxVisible - 1) {
      start = Math.max(0, end - maxVisible + 1);
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  const startItem = currentPage * pageSize + 1;
  const endItem = Math.min((currentPage + 1) * pageSize, totalItems);

  return (
    <PaginationWrap>
      <Pagination>
        <Pagination.First
          disabled={!canPreviousPage}
          className="pagination__item--arrow"
          onClick={() => onPageChange(0)}
        >
          <i className="fi fi-rr-angle-double-small-left" style={{ top: "2px", position: "relative" }} />
        </Pagination.First>
        <Pagination.Prev
          disabled={!canPreviousPage}
          className="pagination__item--arrow"
          onClick={onPreviousPage}
        >
          <i className="fi fi-rr-angle-small-left" style={{ top: "2px", position: "relative" }} />
        </Pagination.Prev>
        {getPageNumbers().map((pageNum) => (
          <Pagination.Item
            key={pageNum}
            active={currentPage === pageNum}
            onClick={() => onPageChange(pageNum)}
          >
            {pageNum + 1}
          </Pagination.Item>
        ))}
        <Pagination.Next
          disabled={!canNextPage}
          className="pagination__item--arrow"
          onClick={onNextPage}
        >
          <i className="fi fi-rr-angle-small-right" style={{ top: "2px", position: "relative" }} />
        </Pagination.Next>
        <Pagination.Last
          disabled={!canNextPage}
          className="pagination__item--arrow"
          onClick={() => onPageChange(totalPages - 1)}
        >
          <i className="fi fi-rr-angle-double-small-right" style={{ top: "2px", position: "relative" }} />
        </Pagination.Last>
      </Pagination>
      <PaginationInfo>
        {t("showing_from_to_no_of_rows", {
          from: startItem,
          to: endItem,
          noOfRows: totalItems,
        })}
      </PaginationInfo>
      {pageSizeOptions.length > 1 && (
        <PaginationSelectWrap>
          <PaginationFormSelect
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {pageSizeOptions.map((size) => (
              <PaginationFormOption key={size} value={size}>
                {t("show_no_of_rows", { noOfRows: size })}
              </PaginationFormOption>
            ))}
          </PaginationFormSelect>
        </PaginationSelectWrap>
      )}
    </PaginationWrap>
  );
};

export default SimplePagination;

// Styled components to match ReactTablePagination styling
const PaginationWrap = styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
  margin-left: 25px;

  @media screen and (max-width: 768px) {
    flex-direction: column-reverse;

    & > * {
      margin: 10px 0;
    }
  }

  .page-item {
    margin-right: 2px;

    .page-link {
      background: transparent;
      border: none;
      color: #6c757d;
      height: 28px;
      font-size: 10px;
      padding: 5px;
      text-align: center;
      min-width: 28px;
      transition: all 0.3s;

      &:hover {
        color: #0056b3;
        background: transparent;
      }

      &:focus,
      &:active {
        box-shadow: none;
      }

      svg {
        width: 13px;
        height: 13px;
        fill: #6c757d;
        transition: 0.3s;
        position: absolute;
        top: 7px;
        left: 8px;
      }
    }

    &.pagination__item--arrow .page-link {
      border-radius: 5px;
      width: 28px;
      height: 28px;

      &:hover {
        background: #f8f9fa;

        svg {
          fill: white;
        }
      }
    }

    &.pagination__item--arrow.disabled .page-link {
      cursor: default;
      opacity: 0.4;
      background: #e9ecef;

      svg {
        fill: #6c757d;
      }
    }

    &.active .page-link {
      background-color: transparent;
      font-weight: bold;
      color: #212529;
    }

    &.disabled .page-link svg {
      fill: #6c757d;
    }
  }
`;

const PaginationInfo = styled.div`
  color: #6c757d;
  font-size: 12px;
  margin-right: 0;
  margin-left: 10px;
`;

const PaginationSelectWrap = styled(PaginationInfo)`
  margin-left: 20px;
  color: #6c757d;
`;

const PaginationFormSelect = styled.select`
  color: #6c757d;
  font-size: 12px;
  cursor: pointer;
  background-color: transparent;
  padding: 6px 10px;
  outline: none;
  border: 1px solid #e9ecef;
  border-radius: 5px;
  background: none;
  box-shadow: none;
  height: 32px;

  &:focus {
    border-color: #0056b3;
  }
`;

const PaginationFormOption = styled.option`
  color: #6c757d;
  font-size: 14px;
`;
